import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  ShoppingCart as ShoppingCartIcon,
  AttachMoney as AttachMoneyIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ru } from 'date-fns/locale';
import api from '../utils/api';

function Statistics() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    views: 0,
    clicks: 0,
    sales: 0,
    revenue: 0
  });
  const [salesData, setSalesData] = useState([]);
  const [startDate, setStartDate] = useState(new Date(new Date().setMonth(new Date().getMonth() - 1)));
  const [endDate, setEndDate] = useState(new Date());

  useEffect(() => {
    fetchStatistics();
  }, [startDate, endDate]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);

      // Форматируем даты для запроса
      const formatDate = (date) => {
        return date.toISOString().split('T')[0];
      };

      // Получаем статистику продаж
      const salesResponse = await api.get(`/statistics/sales?startDate=${formatDate(startDate)}&endDate=${formatDate(endDate)}`);
      const sales = salesResponse.data.rows || [];
      setSalesData(sales);

      // Подсчитываем общую статистику
      const totalSales = sales.length;
      const totalRevenue = sales.reduce((sum, sale) => sum + parseFloat(sale.amount), 0);

      // Получаем общую статистику просмотров и кликов
      const productsResponse = await api.get('/products');
      const products = productsResponse.data;
      const totalViews = products.reduce((sum, product) => sum + (product.views || 0), 0);
      const totalClicks = products.reduce((sum, product) => sum + (product.clicks || 0), 0);

      setStats({
        views: totalViews,
        clicks: totalClicks,
        sales: totalSales,
        revenue: totalRevenue.toFixed(2)
      });
    } catch (error) {
      console.error("Error fetching statistics:", error);
      setError("Failed to load statistics. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <Card 
      sx={{
        height: '100%',
        display: 'flex',
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: theme.shadows[10],
        },
      }}
    >
      <CardContent sx={{ width: '100%', p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Icon sx={{ color: color, fontSize: { xs: 30, sm: 40 }, mr: 1 }} />
          <Typography variant="h6" component="div" sx={{ color: 'text.secondary', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
          {value}
        </Typography>
        <Box
          sx={{
            position: 'absolute',
            right: -20,
            bottom: -20,
            opacity: 0.1,
            transform: 'rotate(-15deg)',
          }}
        >
          <Icon sx={{ fontSize: 100, color: color }} />
        </Box>
      </CardContent>
    </Card>
  );

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography 
        variant="h4" 
        sx={{ 
          mb: 4,
          fontSize: { xs: '1.5rem', sm: '2rem' },
          fontWeight: 'bold'
        }}
      >
        Статистика
      </Typography>

      <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Просмотры"
            value={stats.views}
            icon={VisibilityIcon}
            color={theme.palette.primary.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Клики"
            value={stats.clicks}
            icon={ShoppingCartIcon}
            color={theme.palette.success.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Продажи"
            value={stats.sales}
            icon={AttachMoneyIcon}
            color={theme.palette.warning.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Выручка"
            value={`${stats.revenue} руб.`}
            icon={TrendingUpIcon}
            color={theme.palette.info.main}
          />
        </Grid>
      </Grid>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Период</Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <DatePicker
              label="От"
              value={startDate}
              onChange={setStartDate}
              renderInput={(params) => <TextField {...params} />}
            />
            <DatePicker
              label="До"
              value={endDate}
              onChange={setEndDate}
              renderInput={(params) => <TextField {...params} />}
            />
          </Box>
        </LocalizationProvider>
      </Box>

      <Typography variant="h5" sx={{ mb: 2 }}>История продаж</Typography>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Дата</TableCell>
              <TableCell>Товар</TableCell>
              <TableCell>Покупатель</TableCell>
              <TableCell align="right">Сумма</TableCell>
              <TableCell>ID заказа</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {salesData.map((sale) => (
              <TableRow key={sale.unique_code}>
                <TableCell>{formatDateTime(sale.date)}</TableCell>
                <TableCell>{sale.product_name}</TableCell>
                <TableCell>{sale.buyer_email || 'Не указан'}</TableCell>
                <TableCell align="right">{`${sale.amount} ${sale.currency}`}</TableCell>
                <TableCell>{sale.unique_code}</TableCell>
              </TableRow>
            ))}
            {salesData.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Нет данных о продажах за выбранный период
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default Statistics;
